import { Area } from '@app/domain/Area'
import { Booking } from '@app/domain/Booking'
import { BookingReason } from '@app/domain/BookingReason'
import { BookingSlot } from '@app/domain/BookingSlot'
import {
    Component,
    Input,
    OnChanges, OnInit,
} from '@angular/core'
import { DateTime } from 'luxon'
import { Event } from '@app/domain/VenueEvent'
import { Organisation } from '@app/domain/Organisation'
import { Service } from '@app/domain/Service'
import { QuestionType } from '@app/domain/Question'
import { Venue } from '@app/domain/Venue'
import { PrintColumnKey } from '@app/features/bookings/services/print-preferences.service'
import DineroFactory, { Currency } from 'dinero.js'

@Component({
    selector: 'app-print-booking-diary',
    templateUrl: './print-booking-diary.component.html',
})
export class PrintBookingDiaryComponent implements OnInit, OnChanges {

    @Input() date!: Date
    @Input() organisation!: Organisation
    @Input() venue!: Venue
    @Input() bookings!: Booking[]
    @Input() service: Service | null = null
    @Input() printType: 'all' | 'large-parties' = 'all'
    @Input() largePartyThreshold: number | null = null
    @Input() selectedColumns: PrintColumnKey[] = []

    bookingsOnTables!: {
        booking: Booking,
        reason: BookingReason | null,
        event: Event | null,
        area: Area,
        dietaryRestrictions: string | null,
    }[]
    serviceDescription: {
        name: string,
        start: Date,
        end: Date,
    } | null = null

    // Column visibility properties
    showPhoneColumn!: boolean
    showReasonColumn!: boolean
    showNotesColumn!: boolean
    showDepositColumn!: boolean
    showDietaryColumn!: boolean

    constructor() { }

    ngOnInit() {
        this.injectStylesToIframe()
    }

    ngOnChanges() {
        this.updateColumnVisibility()
        this.updateBookingsOnTables()
    }

    private updateBookingsOnTables() {
        const timeRange = this.timeRangeForSelectedService()
        this.bookingsOnTables = this.bookings
            .filter(booking => {
                if (this.printType === 'large-parties') {
                    return booking.size >= (this.largePartyThreshold || 0)
                }
                return true
            })
            .filter(booking => {
                return timeRange === null || booking.startsBetweenDates(timeRange[0], timeRange[1])
            })
            .flatMap(booking => {
                const reason = booking.reasonId ? this.venue.reasonWithId(booking.reasonId) : null
                const event = booking.eventId ? this.venue.eventWithId(booking.eventId) : null
                const area = this.venue.areaBookingIsIn(booking)
                if (area === null) {
                    return []
                }
                return [{
                    booking: booking,
                    reason: reason,
                    event: event,
                    area: area,
                    dietaryRestrictions: this.getDietaryRestrictions(booking),
                }]
            })
        this.serviceDescription = timeRange !== null ? {
            name: this.service!.displayName,
            start: timeRange[0],
            end: timeRange[1],
        } : null
    }

    private timeRangeForSelectedService(): [Date, Date] | null {
        if (this.service === null) {
            return null
        }
        const allServices = this.organisation.coalescedServicesUsedInVenueOnDate(this.venue, this.date)
        const servicesMatchingSelectedService = allServices
            .filter(({ service }) => {
                return this.service === null || service.displayName === this.service.displayName
            })
        if (servicesMatchingSelectedService.length === 0) {
            return null
        }
        const timeRange = servicesMatchingSelectedService[0].timeRange
        const endBookingSlot = new BookingSlot(timeRange[1])
        const endOfLastBookingSlot = endBookingSlot.endDateTime(this.venue.bookingInterval)
        const laterServiceStartingDuringOurRange = allServices
            .filter(({ service }) => service.displayName !== this.service!.displayName)
            .find(({ timeRange: otherTimeRange }) => {
                return otherTimeRange[0] > timeRange[0] && otherTimeRange[0] < endOfLastBookingSlot
            })

        const effectiveEndTime = laterServiceStartingDuringOurRange
            ? laterServiceStartingDuringOurRange.timeRange[0]
            : endOfLastBookingSlot

        return [timeRange[0], effectiveEndTime]
    }

    private injectStylesToIframe() {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = 'styles.css'
        document.head.appendChild(link)
    }

    private updateColumnVisibility() {
        this.showPhoneColumn = this.selectedColumns.includes('phone')
        this.showReasonColumn = this.selectedColumns.includes('reason') && 
                               (this.venue.reasons.length > 0 || this.venue.events.length > 0)
        this.showNotesColumn = this.selectedColumns.includes('notes')
        this.showDepositColumn = this.selectedColumns.includes('deposit')
        this.showDietaryColumn = this.selectedColumns.includes('dietary')
    }

    getDepositStatus(booking: Booking): string {
        if (booking.deposit === null) {
            return ''
        }
        const amount = DineroFactory({
            amount: booking.deposit.amount,
            currency: booking.deposit.currencyCode as Currency,
        })
        if (booking.deposit.isPaid() && !booking.deposit.isRefunded()) {
            return `Paid: ${amount.toFormat()}`
        }
        if (!booking.deposit.isPaid() && !booking.deposit.isRefunded()) {
            return `Pending: ${amount.toFormat()}`
        }
        if (booking.deposit.isRefunded()) {
            return `Refunded: ${amount.toFormat()}`
        }
        return ''
    }

    private getDietaryRestrictions(booking: Booking): string | null {
        const dietaryValues = booking.answers
            .filter(answer => answer.question.type === QuestionType.DietaryRestrictions)
            .map(answer => answer.answer)
            .filter(answer => answer !== 'None')
            .flatMap(answer => answer.split(','))
            .map(answer => answer.trim())
            .filter(answer => answer.length > 0)

        const textValues = booking.answers
            .filter(answer => answer.question.type === QuestionType.DietaryRestrictionsText)
            .map(answer => answer.answer)
            .filter(answer => answer !== 'None')
            .map(answer => answer.trim())
            .filter(answer => answer.length > 0)

        const allValues = [...dietaryValues, ...textValues]

        if (allValues.length === 0) {
            return null
        }
        return allValues.join(', ')
    }

    protected readonly DateTime = DateTime
}
